<script setup lang="ts">
import OrderForm from '@/components/features/orders/OrderForm.vue'
import CustomerDetailsForm from '@/components/features/orders/CustomerDetailsForm.vue'
import ProductDetailsForm from '@/components/features/orders/ProductDetailsForm.vue'
import { useRouter } from 'vue-router'
import { useOrderForm } from '@/composables/useOrderForm'
import { Icon } from '@iconify/vue'
import Button from 'primevue/button'
import { editOrder } from '@/api/orders'
import { computed, ref, onMounted } from 'vue'
import { getCustomerList } from '@/api/customers'
import {type Customer} from '@/types/customer-types'
import  { type ErrorResponse } from '@/types/error-types'
import FormTextArea from '@/components/inputs/FormTextArea.vue'

const {
  values,
  loading,
  handleSubmit,
  setErrors,
  formType,
  showToast,
  newProduct,
  removeProduct,
  productFields
} = useOrderForm('edit')

const router = useRouter()
const orderId = router.currentRoute.value.params.id as string
const showRemoveButton = computed(() => productFields.value.length > 1)
const customerList = ref<Customer[]>([])

const onSubmit = handleSubmit(async () => {
  loading.value = true

  await editOrder(values, orderId)
    .then(() => {
      loading.value = false
      showToast('info', 'Order Updated!', 'You have successfully updated the order.')
      router.push('/orders')
    })
      .catch((error: { response?: { data: ErrorResponse } }) => {
        loading.value = false
        setErrors(error)
        const errorMessages = error.response?.data?.errors?.map((err) => err.description).join('\n') || 'Unable to edit order.'
        showToast('error', 'An error occurred!', errorMessages)
      })
})

onMounted(async () => {
  if (productFields.value.length === 0) {
    newProduct()
  }
  customerList.value = await getCustomerList()
})
</script>

<template>
  <div class="flex justify-between items-center mb-10">
    <Button
      link
      @click="router.back()"
      class="flex items-center gap-x-2 group hover:text-primary-default"
    >
      <Icon
        icon="material-symbols:arrow-back-rounded"
        class="text-3xl group-hover:translate-x-2 transition-transform"
      />
      <span>Back</span>
    </Button>

    <h1 class="font-semibold text-2xl">Editing Customer Order</h1>

    <Button label="Save Order" @click="onSubmit">
      <template #icon>
        <Icon icon="material-symbols:save" class="text-xl ml-3 order-1" />
      </template>
    </Button>
  </div>
  <div class="bg-white rounded-xl pb-2">
    <OrderForm :type="formType" :values="values" :onSubmit="onSubmit" :setErrors="setErrors" />
    <CustomerDetailsForm
      :type="formType"
      :values="values"
      :onSubmit="onSubmit"
      :setErrors="setErrors"
      :customer-list="customerList"
    />
    <p class="text-lg font-bold ml-7">Product Details ({{ productFields.length }})</p>
    <template v-for="(product, index) in productFields" :key="product.key">
      <ProductDetailsForm
        :type="formType"
        :formPath="`${index}`"
        :setErrors="setErrors"
        :values="values"
        :productFields="values.orderProducts[index]"
        :removeProduct="() => removeProduct(index)"
        :showRemoveButton="showRemoveButton"
        :newProduct="newProduct"
        :customerList="customerList"
      />
    </template>
    <div class="p-5">
      <FormTextArea
        label="Delivery Information"
        inputName="deliveryInformation"
        id="deliveryInformation"
      />
    </div>
  </div>
</template>
